/**
 * Whether the main sidebar is collapsed to its icon rail.
 *
 * - The editor's choice is remembered in localStorage.
 * - Until they choose, the rail follows the window: collapsed below 1440 px,
 *   where the three-column Article Generator otherwise leaves the article
 *   itself too little room.
 * - ⌘B / Ctrl+B toggles it, as in VS Code. Plain textareas have no use for the
 *   chord, so it works while typing too.
 */

import { useCallback, useEffect, useState } from 'react'

export const SIDEBAR_STORAGE_KEY = 'hkcm.sidebar.collapsed'
export const NARROW_QUERY = '(max-width: 1439px)'

function savedPreference(): boolean | null {
    try {
        const saved = window.localStorage.getItem(SIDEBAR_STORAGE_KEY)
        if (saved === 'true') return true
        if (saved === 'false') return false
    } catch {
        // Storage can be unavailable (private mode); fall back to the window.
    }
    return null
}

function isNarrow(): boolean {
    return window.matchMedia?.(NARROW_QUERY).matches ?? false
}

export function isToggleShortcut(event: KeyboardEvent): boolean {
    return (event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'b'
}

export function useSidebarCollapsed(): [boolean, () => void] {
    const [collapsed, setCollapsed] = useState<boolean>(() => savedPreference() ?? isNarrow())

    const toggle = useCallback(() => {
        const next = !collapsed
        setCollapsed(next)
        try {
            window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next))
        } catch {
            // Not remembering the choice is harmless.
        }
    }, [collapsed])

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (!isToggleShortcut(event)) return
            event.preventDefault()
            toggle()
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [toggle])

    useEffect(() => {
        const query = window.matchMedia?.(NARROW_QUERY)
        if (!query?.addEventListener) return
        const onChange = (event: MediaQueryListEvent) => {
            if (savedPreference() === null) setCollapsed(event.matches)
        }
        query.addEventListener('change', onChange)
        return () => query.removeEventListener('change', onChange)
    }, [])

    return [collapsed, toggle]
}
